import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Settings, Menu, X, ArrowRightLeft, Database, Bell, History, AlertTriangle, Cloud, Shield, GitBranch, BookOpen } from 'lucide-react';
import Logo from './images/logo.svg';
import LogoSquare from './images/logo-square.svg';
import Comparator from './components/Comparator';
import ContractsManager from './components/ContractsManager';
import VersionHistory from './components/VersionHistory';
import SettingsModal, { DEFAULT_SETTINGS } from './components/SettingsModal';
import UserMenu from './components/UserMenu';
import { OrganizationProvider, useOrganization } from './components/OrganizationProvider';
import { ToastContainer } from './components/Toast';
import LoadingScreen from './components/LoadingScreen';
import { useAuthState, isDevMode, SignIn } from './components/ClerkProviderWrapper';
import { dark } from '@clerk/themes';
import { setAuthTokenGetter } from './authClient';
import NotificationLog from './components/NotificationLog';
import DriftInbox from './components/DriftInbox';
import { getDriftEventContent } from './api';

import PipelineManager from './components/PipelineManager';
import AdminDashboard from './components/AdminDashboard';


/**
 * Main application content that uses the organization context.
 */
function AppContent() {
  const [view, setView] = useState(() => {
    try {
      const saved = localStorage.getItem('driftops_last_view');
      // Validate that the saved view is one of the allowed views
      const allowedViews = ['comparator', 'contracts', 'versions', 'inbox', 'pipelines', 'admin'];
      return (saved && allowedViews.includes(saved)) ? saved : 'comparator';
    } catch {
      return 'comparator';
    }
  });

  // Persist view to localStorage
  useEffect(() => {
    localStorage.setItem('driftops_last_view', view);
  }, [view]);
  const [showLoader, setShowLoader] = useState(true);
  const [loaderFading, setLoaderFading] = useState(false);
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notificationLogOpen, setNotificationLogOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [reviewDriftEvent, setReviewDriftEvent] = useState(null); // Drift event to review in Comparator
  const [isFullscreen, setIsFullscreen] = useState(false); // Comparator fullscreen mode
  const [preSelectContractId, setPreSelectContractId] = useState(null); // Contract to pre-select in Comparator
  const [preSelectVersionsContractId, setPreSelectVersionsContractId] = useState(null); // Contract to pre-select in Versions

  const { getToken, isSignedIn, isLoaded } = useAuthState();
  const { isAdmin } = useOrganization();
  const isDevelopment = isDevMode();



  // Set up auth token getter for API calls
  // Use a ref to avoid dependency issues with unstable getToken function
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    // Wrap the token getter to always use the latest version
    setAuthTokenGetter(() => getTokenRef.current());
  }, []); // Only run once on mount

  // Settings with localStorage persistence
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('driftops_settings');
      return saved
        ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) }
        : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('driftops_settings', JSON.stringify(settings));
  }, [settings]);

  // Ensure dark mode is always applied to the document root
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Need a separate history state to keep them after dismissal
  const [history, setHistory] = useState([]);
  const recentToastsRef = useRef([]);

  const addToast = useCallback((message, type = 'info', options = {}) => {
    const now = Date.now();
    // Clean up old toast records (older than 2 seconds) to keep the ref array small
    recentToastsRef.current = recentToastsRef.current.filter(t => now - t.time < 2000);

    // Skip dedup for action toasts: each one carries a per-event callback (e.g. Undo)
    // that must not be collapsed with a sibling, otherwise the user loses recovery.
    if (!options.action) {
      if (recentToastsRef.current.some(t => t.message === message && t.type === type)) {
        return;
      }
      recentToastsRef.current.push({ message, type, time: now });
    }

    const id = `${now}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast = { id, message, type, timestamp: new Date(), ...options };
    setToasts(prev => [...prev, newToast]);
    setHistory(prev => [...prev, newToast]);
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Memoize callback to prevent infinite loop with VersionHistory
  const handleContractChanged = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Require authentication for all views EXCEPT the comparator and login pages
  useEffect(() => {
    if (isLoaded && !isSignedIn && !isDevelopment && view !== 'comparator' && view !== 'login') {
      setView('comparator');
    }
    // Automatically return to comparator once user successfully signs in from the login view
    if (isLoaded && isSignedIn && view === 'login') {
      setView('comparator');
    }
  }, [isLoaded, isSignedIn, isDevelopment, view]);

  // Fade out and unmount loader when auth is ready
  useEffect(() => {
    if (isLoaded && showLoader) {
      setLoaderFading(true);
      const t = setTimeout(() => setShowLoader(false), 400);
      return () => clearTimeout(t);
    }
  }, [isLoaded]);

  if (!isLoaded) return <LoadingScreen fading={false} />;

  return (
    <>
      {showLoader && <LoadingScreen fading={loaderFading} />}
      <div className="flex flex-col h-screen bg-[#1e1e1e] font-sans text-[#cccccc]">
        <ToastContainer toasts={toasts} removeToast={removeToast} />

        {/* Header - hidden in fullscreen mode */}
        {!isFullscreen && (
          <header className="bg-[#252526] border-b border-[#3c3c3c] pl-2 pr-4 md:pr-6 py-2 flex items-center justify-between shadow-md z-20 relative">
            {/* Left section - Logo */}
            <div className="flex items-center cursor-pointer shrink-0 h-full" onClick={() => setView('comparator')}>
              <img src={Logo} alt="DriftOps Logo" className="h-[48px] w-auto logo-enter" />
            </div>

            {/* Center section - Navigation */}
            <nav className="flex items-center gap-1 bg-[#3c3c3c] p-1 rounded-lg shrink-0">
              {/* Main Navigation - Always visible */}
              <button
                onClick={() => setView('comparator')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer select-none whitespace-nowrap ${view === 'comparator' ? 'bg-[#252526] shadow text-blue-400' : 'text-[#808080] hover:text-[#cccccc]'}`}
              >
                <ArrowRightLeft size={16} className="shrink-0" />
                Comparator
              </button>

              <button
                onClick={() => (isSignedIn || isDevelopment) ? setView('contracts') : setView('login')}
                title={(isSignedIn || isDevelopment) ? undefined : 'Sign in to access'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer select-none whitespace-nowrap ${
                  (isSignedIn || isDevelopment)
                    ? (view === 'contracts' ? 'bg-[#252526] shadow text-blue-400' : 'text-[#808080] hover:text-[#cccccc]')
                    : 'text-[#555555] opacity-60'
                }`}
              >
                <Database size={16} className="shrink-0" />
                Contracts
              </button>

              <button
                onClick={() => (isSignedIn || isDevelopment) ? setView('inbox') : setView('login')}
                title={(isSignedIn || isDevelopment) ? undefined : 'Sign in to access'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer select-none whitespace-nowrap ${
                  (isSignedIn || isDevelopment)
                    ? (view === 'inbox' ? 'bg-[#252526] shadow text-blue-400' : 'text-[#808080] hover:text-[#cccccc]')
                    : 'text-[#555555] opacity-60'
                }`}
              >
                <AlertTriangle size={16} className="shrink-0" />
                Drift Inbox
              </button>

              {/* Divider for visual separation */}
              <div className="w-px h-6 bg-[#555555] mx-1 hidden xl:block" />

              {/* Extra Navigation - Only on very large screens */}
              <button
                onClick={() => (isSignedIn || isDevelopment) ? setView('versions') : setView('login')}
                title={(isSignedIn || isDevelopment) ? undefined : 'Sign in to access'}
                className={`hidden xl:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer select-none whitespace-nowrap ${
                  (isSignedIn || isDevelopment)
                    ? (view === 'versions' ? 'bg-[#252526] shadow text-blue-400' : 'text-[#808080] hover:text-[#cccccc]')
                    : 'text-[#555555] opacity-60'
                }`}
              >
                <History size={16} />
                Versions
              </button>

              <button
                onClick={() => (isSignedIn || isDevelopment) ? setView('pipelines') : setView('login')}
                title={(isSignedIn || isDevelopment) ? undefined : 'Sign in to access'}
                className={`hidden xl:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer select-none whitespace-nowrap ${
                  (isSignedIn || isDevelopment)
                    ? (view === 'pipelines' ? 'bg-[#252526] shadow text-blue-400' : 'text-[#808080] hover:text-[#cccccc]')
                    : 'text-[#555555] opacity-60'
                }`}
              >
                <GitBranch size={16} />
                Pipelines
              </button>
            </nav>

            {/* Right section - User controls */}
            <div className="flex items-center gap-2 md:gap-4 justify-end shrink-0">

              {/* Hamburger Menu - Hidden on very large screens */}
              <div className="relative xl:hidden">
                <button
                  onClick={() => setHamburgerMenuOpen(!hamburgerMenuOpen)}
                  className={`p-2 rounded-full hover:bg-[#3c3c3c] transition-colors ${hamburgerMenuOpen ? 'bg-[#3c3c3c] text-blue-400' : 'text-[#cccccc]'}`}
                  title="More"
                >
                  <Menu size={20} />
                </button>

                {hamburgerMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setHamburgerMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-[#252526] rounded-xl border border-[#3c3c3c] shadow-xl z-20 py-2">
                      <button
                        onClick={() => { (isSignedIn || isDevelopment) ? setView('versions') : setView('login'); setHamburgerMenuOpen(false); }}
                        title={(isSignedIn || isDevelopment) ? undefined : 'Sign in to access'}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                          (isSignedIn || isDevelopment)
                            ? (view === 'versions' ? 'bg-blue-900/20 text-blue-400' : 'text-[#cccccc] hover:bg-[#3c3c3c]')
                            : 'text-[#555555] opacity-60'
                        }`}
                      >
                        <History size={18} />
                        Versions
                      </button>
                      <button
                        onClick={() => { (isSignedIn || isDevelopment) ? setView('pipelines') : setView('login'); setHamburgerMenuOpen(false); }}
                        title={(isSignedIn || isDevelopment) ? undefined : 'Sign in to access'}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                          (isSignedIn || isDevelopment)
                            ? (view === 'pipelines' ? 'bg-blue-900/20 text-blue-400' : 'text-[#cccccc] hover:bg-[#3c3c3c]')
                            : 'text-[#555555] opacity-60'
                        }`}
                      >
                        <GitBranch size={18} />
                        Pipelines
                      </button>

                      {/* Mobile-only: Notifications, Docs, and Settings */}
                      <div className="border-t border-[#3c3c3c] mt-2 pt-2 md:hidden">
                        {(isSignedIn || isDevelopment) && (
                          <button
                            onClick={() => { setNotificationLogOpen(true); setHamburgerMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#cccccc] hover:bg-[#3c3c3c] transition-colors"
                          >
                            <Bell size={18} />
                            Notifications
                          </button>
                        )}
                        <a
                          href="https://www.driftops.io/docs"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setHamburgerMenuOpen(false)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#cccccc] hover:bg-[#3c3c3c] transition-colors"
                        >
                          <BookOpen size={18} />
                          Documentation
                        </a>
                        <button
                          onClick={() => { setSettingsOpen(true); setHamburgerMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#cccccc] hover:bg-[#3c3c3c] transition-colors"
                        >
                          <Settings size={18} />
                          Settings
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Notifications - Desktop only (not on mobile hamburger) */}
              {(isSignedIn || isDevelopment) && (
                <button
                  onClick={() => setNotificationLogOpen(!notificationLogOpen)}
                  className={`p-2 rounded-full hover:bg-[#3c3c3c] transition-colors relative ${notificationLogOpen ? 'bg-[#3c3c3c] text-blue-400' : 'text-[#cccccc]'} hidden md:block`}
                  title="Notification Log"
                >
                  <Bell size={20} />
                </button>
              )}

              {/* Admin Dashboard - authenticated only */}
              {(isSignedIn || isDevelopment) && (
                <button
                  onClick={() => setView('admin')}
                  className={`p-2 rounded-full hover:bg-[#3c3c3c] transition-colors ${view === 'admin' ? 'bg-[#3c3c3c] text-blue-400' : 'text-[#cccccc]'} hidden md:block`}
                  title="Admin Dashboard"
                >
                  <Shield size={20} />
                </button>
              )}

              {/* Docs - Desktop only */}
              <a
                href="https://www.driftops.io/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-[#3c3c3c] text-[#cccccc] transition-colors hidden md:block"
                title="Documentation"
              >
                <BookOpen size={20} />
              </a>

              {/* Settings - Desktop only (not on mobile hamburger) */}
              <button
                onClick={() => setSettingsOpen(true)}
                className="p-2 rounded-full hover:bg-[#3c3c3c] text-[#cccccc] transition-colors hidden md:block"
                title="Settings"
              >
                <Settings size={20} />
              </button>

              <div className="flex items-center justify-center">
                {(isSignedIn || isDevelopment) ? (
                  <UserMenu />
                ) : (
                  <button
                    onClick={() => setView('login')}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded font-medium text-sm transition-colors shadow-lg pointer-events-auto"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>

            <NotificationLog
              isOpen={notificationLogOpen}
              onClose={() => setNotificationLogOpen(false)}
              notifications={history}
              onClear={clearHistory}
            />
          </header>
        )}

        {/* Settings Modal */}
        <SettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settings={settings}
          onSettingsChange={setSettings}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-hidden relative">
          <div className={`absolute inset-0 flex flex-col ${view === 'comparator' ? 'z-10' : 'z-0 invisible'}`}>
            <Comparator
              isAuthenticated={(isLoaded && (isSignedIn || isDevelopment))}
              addToast={addToast}
              onContractSaved={() => setRefreshTrigger(prev => prev + 1)}
              refreshTrigger={refreshTrigger}
              matchingSettings={{
                ...settings,
              }}
              reviewDriftEvent={reviewDriftEvent}
              onReviewComplete={() => setReviewDriftEvent(null)}
              onFullscreenChange={setIsFullscreen}
              preSelectContractId={preSelectContractId}
              onPreSelectConsumed={() => setPreSelectContractId(null)}
              onSignInClick={() => setView('login')}
            />
          </div>

          {(isSignedIn || isDevelopment) && (
            <>
              <div className={`absolute inset-0 bg-[#1e1e1e] ${view === 'contracts' ? 'z-10' : 'z-0 invisible'}`}>
                <div className="h-full overflow-y-auto">
                  <ContractsManager
                    addToast={addToast}
                    refreshTrigger={refreshTrigger}
                    onContractChanged={handleContractChanged}
                    onNavigateToComparator={(contractId, driftEvent) => {
                      if (driftEvent) {
                        setReviewDriftEvent(driftEvent);
                      }
                      if (contractId) {
                        setPreSelectContractId(contractId);
                      }
                      setView('comparator');
                    }}
                    onNavigateToVersions={(contractId) => {
                      if (contractId) {
                        setPreSelectVersionsContractId(contractId);
                      }
                      setView('versions');
                    }}
                  />
                </div>
              </div>

              <div className={`absolute inset-0 bg-[#1e1e1e] ${view === 'versions' ? 'z-10' : 'z-0 invisible'}`}>
                <VersionHistory
                  addToast={addToast}
                  contractId={preSelectVersionsContractId}
                  onContractChanged={handleContractChanged}
                  onNavigateToComparator={(contractId, oldVersion, newVersion) => {
                    // Create a drift-event-like object to load in comparator
                    setReviewDriftEvent({
                      source_content: oldVersion.schema_content,
                      target_content: newVersion.schema_content,
                      source_name: `Version ${oldVersion.version_number}`,
                      target_name: `Version ${newVersion.version_number}`,
                    });
                    setView('comparator');
                  }}
                />
              </div>

              <div className={`absolute inset-0 bg-[#1e1e1e] ${view === 'inbox' ? 'z-10' : 'z-0 invisible'}`}>
                <div className="h-full overflow-y-auto p-6">
                  <DriftInbox
                    addToast={addToast}
                    refreshTrigger={refreshTrigger}
                    onReviewClick={async (event) => {
                      try {
                        // Fetch full content — list endpoint excludes source_content/target_content/diff_report for performance
                        const content = await getDriftEventContent(event.id, 'all');
                        setReviewDriftEvent({
                          ...event,
                          source_content: content.source_content,
                          target_content: content.target_content,
                          diff_report: content.diff_report,
                        });
                        setView('comparator');
                      } catch (err) {
                        console.error('Failed to load drift event content:', err);
                        addToast('Failed to load drift event content', 'error');
                      }
                    }}
                  />
                </div>
              </div>

              <div className={`absolute inset-0 bg-[#1e1e1e] ${view === 'pipelines' ? 'z-10' : 'z-0 invisible'}`}>
                <div className="h-full overflow-y-auto p-6">
                  <PipelineManager addToast={addToast} />
                </div>
              </div>

              <div className={`absolute inset-0 bg-[#1e1e1e] ${view === 'admin' ? 'z-10' : 'z-0 invisible'}`}>
                <div className="h-full overflow-y-auto">
                  <AdminDashboard />
                </div>
              </div>
            </>
          )
          }

          {/* Login View */}
          <div className={`absolute inset-0 flex items-center justify-center bg-[#1e1e1e] transition-opacity duration-200 ${view === 'login' ? 'z-50 opacity-100' : 'z-0 opacity-0 pointer-events-none'}`}>
            <button
              onClick={() => setView('comparator')}
              className="absolute top-4 left-4 text-[#808080] hover:text-white flex items-center gap-2 text-sm z-10"
            >
              &larr; Back to Comparator
            </button>

            <div className="flex flex-col items-center">
              <img src={LogoSquare} alt="DriftOps" className="w-24 h-24 rounded-lg mb-6" />
              {view === 'login' && !isSignedIn && (
                <SignIn
                  routing="hash"
                  appearance={{
                    baseTheme: dark,
                    elements: {
                      formButtonPrimary: "bg-blue-600 hover:bg-blue-500 text-white rounded shadow-sm transition-colors",
                      footerActionLink: "text-blue-400 hover:text-blue-300",
                      footer: "hidden"
                    },
                    variables: {
                      colorPrimary: '#2563eb',
                      borderRadius: '0.5rem',
                      colorBackground: '#252526',
                      colorInputBackground: '#1e1e1e',
                      colorText: '#ffffff',
                      colorTextSecondary: '#9ca3af',
                      colorTextOnPrimaryBackground: '#ffffff',
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div >
      </div >
    </>
  );
}

function App() {
  return (
    <OrganizationProvider>
      <MemoizedAppContent />
    </OrganizationProvider>
  );
}

// Memoize AppContent to prevent unnecessary re-renders from parent
const MemoizedAppContent = React.memo(AppContent);

export default App;
